<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="$emit('close')">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
      <div class="flex justify-between items-center p-6 border-b">
        <h2 class="text-2xl font-bold text-purple-700">
          <i class="fa fa-car mr-2"></i>Parktickets hochladen
        </h2>
        <button @click="$emit('close')" class="text-gray-700 hover:text-black text-3xl iconbutton admin-button--ghost admin-button--icon" aria-label="Schließen" title="Schließen">
          <i class="fa fa-xmark"></i>
        </button>
      </div>

      <div class="p-6 space-y-4">
        <div class="p-4 bg-purple-50 border border-purple-200 rounded">
          <h3 class="font-semibold mb-2">Zuordnung:</h3>
          <div class="text-sm text-gray-700">
            <p><strong>Einsatzstelle:</strong> {{ context.einsatzstelle_name }}</p>
            <p class="text-xs text-gray-500">Code: {{ context.einsatzstelle_code }}</p>
          </div>
        </div>

        <div>
          <label class="block text-sm font-semibold mb-2">
            Parkticket-Dateien (Bilder oder PDFs):
          </label>
          <div 
            @paste="handlePaste"
            @drop.prevent="handleDrop"
            @dragover.prevent
            class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors"
          >
            <input 
              type="file"
              ref="fileInput"
              @change="handleFileSelect"
              accept="image/*,application/pdf"
              multiple
              class="hidden"
            />
            <button 
              @click="$refs.fileInput.click()"
              class="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-200 font-semibold admin-button--secondary"
            >
              <i class="fa fa-folder-open mr-2"></i>Dateien auswählen
            </button>
            <p class="text-sm text-gray-600 mt-2">
              oder Dateien hierher ziehen oder einfügen (Strg+V)
            </p>
          </div>
        </div>

        <div v-if="selectedFiles.length > 0" class="space-y-2">
          <h4 class="font-semibold text-sm">Ausgewählte Dateien ({{ selectedFiles.length }}):</h4>
          <div class="max-h-40 overflow-y-auto space-y-1">
            <div 
              v-for="(file, index) in selectedFiles" 
              :key="index"
              class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2"
            >
              <span class="text-sm truncate flex-1">{{ file.name }}</span>
              <button
                @click="removeFile(index)"
                class="text-red-600 hover:text-red-800 ml-2 iconbutton admin-button--ghost admin-button--icon"
                title="Entfernen" aria-label="Entfernen"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="uploading" class="p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
          <i class="fa fa-spinner fa-spin mr-2"></i>Hochladen... {{ uploadProgress }} / {{ selectedFiles.length }}
        </div>

        <div v-if="errorMessage" class="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {{ successMessage }}
        </div>
      </div>

      <div class="flex justify-end gap-3 p-6 border-t">
        <button 
          @click="$emit('close')" 
          class="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 font-semibold admin-button--secondary">
          Abbrechen
        </button>
        <button 
          @click="uploadAll" 
          :disabled="selectedFiles.length === 0 || uploading"
          class="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed admin-button--primary">
          <i class="fa fa-upload mr-2"></i>{{ selectedFiles.length }} Ticket(s) hochladen
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from '../CASP';

export default {
  name: 'ParkingTicketUploadModal',
  props: {
    context: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedFiles: [],
      errorMessage: '',
      successMessage: '',
      uploading: false,
      uploadProgress: 0
    };
  },
  mounted() {
    window.addEventListener('paste', this.handlePaste);
  },
  beforeUnmount() {
    window.removeEventListener('paste', this.handlePaste);
  },
  methods: {
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFiles(files);
    },
    handlePaste(event) {
      const items = event.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      
      if (files.length > 0) {
        event.preventDefault();
        this.addFiles(files);
      }
    },
    handleDrop(event) {
      const files = Array.from(event.dataTransfer.files);
      this.addFiles(files);
    },
    addFiles(files) {
      const validFiles = files.filter(file => {
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';
        return isImage || isPdf;
      });
      
      this.selectedFiles.push(...validFiles);
      
      if (validFiles.length < files.length) {
        this.errorMessage = 'Einige Dateien wurden übersprungen (nur Bilder und PDFs erlaubt)';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    async uploadAll() {
      if (this.selectedFiles.length === 0) {
        this.errorMessage = 'Bitte wählen Sie mindestens eine Datei aus';
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';
      this.uploading = true;
      this.uploadProgress = 0;

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < this.selectedFiles.length; i++) {
        const file = this.selectedFiles[i];
        
        try {
          await this.uploadSingleFile(file);
          successCount++;
          this.uploadProgress = i + 1;
        } catch (error) {
          errorCount++;
          console.error('Upload failed for file:', file.name, error);
        }
      }

      this.uploading = false;

      if (successCount > 0) {
        this.successMessage = `${successCount} Parkticket(s) erfolgreich hochgeladen`;
        if (errorCount > 0) {
          this.successMessage += ` (${errorCount} fehlgeschlagen)`;
        }
        
        setTimeout(() => {
          this.$emit('save');
          this.$emit('close');
        }, 1500);
      } else {
        this.errorMessage = 'Fehler beim Hochladen aller Dateien';
      }
    },
    uploadSingleFile(file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('einsatzstelle_code', this.context.einsatzstelle_code);

        CASP.createParkingTicket(formData, (status, response) => {
          if (status === 200 || status === 201) {
            resolve(response);
          } else {
            reject(response);
          }
        });
      });
    }
  }
};
</script>
